import { GOOGLE_MAPS_API_KEY } from 'components/globals';

export type StreetViewAddress = {
  street: string;
  city: string;
  state: string;
};

export type StreetViewUrlOptions = {
  /** Location to photograph, geocoded by Google from its street/city/state parts. */
  address: StreetViewAddress;
  /** Rendered width in CSS pixels. Scaled down with `height` to stay within Google's cap. */
  width: number;
  /** Rendered height in CSS pixels. Scaled down with `width` to stay within Google's cap. */
  height: number;
};

/** Street View serves at most 640px per side. */
const MAX_STREET_VIEW_SIDE = 640;

const locationParam = ({ street, city, state }: StreetViewAddress): string =>
  `${street}, ${city}, ${state}`;

const scaleToFit = ({
  width,
  height,
  maxSide,
}: {
  width: number;
  height: number;
  /** Largest pixel size Google serves per side; the request is scaled under it, keeping ratio. */
  maxSide: number;
}): { width: number; height: number } => {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
};

/**
 * Google Street View static image URL.
 *
 * `return_error_code=true` makes a missing panorama a 4xx so an `onError` handler can fall back.
 * {@link googleStreetViewMetadataUrl} is still the cheap check used to hide the photo before that
 * 4xx arrives.
 */
export const googleStreetViewUrl = ({ address, width, height }: StreetViewUrlOptions): string => {
  const size = scaleToFit({ width, height, maxSide: MAX_STREET_VIEW_SIDE });

  return (
    'https://maps.googleapis.com/maps/api/streetview?' +
    `&size=${size.width}x${size.height}` +
    `&location=${encodeURIComponent(locationParam(address))}` +
    `&return_error_code=true` +
    `&key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}`
  );
};

/**
 * Reports whether a panorama exists, as `{ status: 'OK' | 'ZERO_RESULTS' | ... }`. Free, and does
 * not count against the Street View quota. Takes the same location as
 * {@link googleStreetViewUrl} so the two cannot resolve to different places.
 */
export const googleStreetViewMetadataUrl = (address: StreetViewAddress): string =>
  'https://maps.googleapis.com/maps/api/streetview/metadata?' +
  `location=${encodeURIComponent(locationParam(address))}` +
  `&key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}`;
